<template>
    <div
        v-if="visible"
        class="draggable-postit"
        :class="{ minimized: isMinimized, 'no-transition': isDragging || isResizing }"
        :style="postitStyle"
        @mousedown="bringToFront"
    >
        <!-- Resize Handles -->
        <div v-show="!isMinimized" class="resize-handle top-left" @mousedown.stop="startResize($event, 'tl')"></div>
        <div v-show="!isMinimized" class="resize-handle top-right" @mousedown.stop="startResize($event, 'tr')"></div>
        <div v-show="!isMinimized" class="resize-handle bottom-left" @mousedown.stop="startResize($event, 'bl')"></div>
        <div v-show="!isMinimized" class="resize-handle bottom-right" @mousedown.stop="startResize($event, 'br')"></div>

        <!-- Post-it Header (Draggable Handle) -->
        <div class="postit-header" @mousedown.stop="startDrag">
            <div class="header-actions">
                <button class="btn-action minimize" @click.stop="toggleMinimize" :title="$t('common.minimize')">
                    <Minus v-if="!isMinimized" :size="14" />
                    <Square v-else :size="14" />
                </button>
            </div>
            <div class="drag-handle">
                <GripHorizontal :size="16" />
            </div>
            <div class="header-actions">
                <button class="btn-action close" @click.stop="$emit('update:visible', false)" :title="$t('common.close')">
                    <X :size="14" />
                </button>
            </div>
        </div>

        <!-- Post-it Content -->
        <div v-show="!isMinimized" class="postit-body">
            <textarea
                class="postit-textarea"
                :value="value"
                :readonly="readonly"
                :title="readonly ? title : null"
                @input="onInput"
                :placeholder="$t('devis.v3.note_placeholder')"
            ></textarea>
        </div>

        <!-- Minimized Indicator -->
        <div v-show="isMinimized" class="postit-minimized-label" @click="toggleMinimize">
            {{ $t('devis.v3.notes_header') }}
        </div>
    </div>
</template>

<script>
import { GripHorizontal, Minus, X, Square } from 'lucide-vue'

export default {
    name: 'DraggablePostIt',
    components: { GripHorizontal, Minus, X, Square },
    props: {
        value: {
            type: String,
            default: '',
        },
        visible: {
            type: Boolean,
            default: true,
        },
        // Mode lecture seule : textarea non éditable (ex. devis accepté/verrouillé).
        readonly: {
            type: Boolean,
            default: false,
        },
        // Tooltip affiché au survol du textarea quand il est en lecture seule.
        title: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            isMinimized: false,
            isDragging: false,
            isResizing: false,
            resizeDir: '',
            x: null, // Let CSS handle initial center
            y: null,
            width: null,
            height: null,
            offsetX: 0,
            offsetY: 0,
            zIndex: 1000,
            startX: 0,
            startY: 0,
            initialWidth: 0,
            initialHeight: 0,
            initialX: 0,
            initialY: 0,
        }
    },
    computed: {
        postitStyle() {
            const style = {
                zIndex: this.zIndex,
            }
            if (this.x !== null) {
                style.left = `${this.x}px`
                style.transform = 'none' // Remove CSS translate once we have real coordinates
            }
            if (this.y !== null) {
                style.top = `${this.y}px`
            }
            if (!this.isMinimized) {
                if (this.width !== null) {
                    style.width = `${this.width}px`
                    style.minWidth = '200px'
                }
                if (this.height !== null) {
                    style.height = `${this.height}px`
                }
            }
            return style
        },
    },
    methods: {
        onInput(e) {
            // En lecture seule, aucune modification n'est propagée.
            if (this.readonly) return
            this.$emit('input', e.target.value)
        },
        toggleMinimize() {
            this.isMinimized = !this.isMinimized
        },
        startDrag(e) {
            if (this.x === null || this.y === null) {
                const rect = this.$el.getBoundingClientRect()
                this.x = rect.left
                this.y = rect.top
            }
            this.isDragging = true
            this.offsetX = e.clientX - this.x
            this.offsetY = e.clientY - this.y
            this.bringToFront()

            window.addEventListener('mousemove', this.onDrag)
            window.addEventListener('mouseup', this.stopDrag)
        },
        onDrag(e) {
            if (!this.isDragging) return
            this.x = e.clientX - this.offsetX
            this.y = e.clientY - this.offsetY

            // Contain within viewport (basic)
            const padding = 10
            if (this.x < padding) this.x = padding
            if (this.y < padding) this.y = padding
            if (this.x > window.innerWidth - 50) this.x = window.innerWidth - 50
            if (this.y > window.innerHeight - 50) this.y = window.innerHeight - 50
        },
        stopDrag() {
            this.isDragging = false
            window.removeEventListener('mousemove', this.onDrag)
            window.removeEventListener('mouseup', this.stopDrag)
        },
        startResize(e, dir) {
            if (this.x === null || this.y === null) {
                const rect = this.$el.getBoundingClientRect()
                this.x = rect.left
                this.y = rect.top
            }
            if (this.width === null || this.height === null) {
                this.width = this.$el.offsetWidth
                this.height = this.$el.offsetHeight
            }
            this.isResizing = true
            this.resizeDir = dir
            this.startX = e.clientX
            this.startY = e.clientY
            this.initialWidth = this.width
            this.initialHeight = this.height
            this.initialX = this.x
            this.initialY = this.y
            this.bringToFront()

            window.addEventListener('mousemove', this.onResize)
            window.addEventListener('mouseup', this.stopResize)
        },
        onResize(e) {
            if (!this.isResizing) return
            const dx = e.clientX - this.startX
            const dy = e.clientY - this.startY

            let newWidth = this.initialWidth
            let newHeight = this.initialHeight
            let newX = this.initialX
            let newY = this.initialY

            const minWidth = 250
            const minHeight = 150

            if (this.resizeDir === 'br') {
                newWidth = this.initialWidth + dx
                newHeight = this.initialHeight + dy
            } else if (this.resizeDir === 'tr') {
                newWidth = this.initialWidth + dx
                newHeight = this.initialHeight - dy
                if (newHeight >= minHeight) {
                    newY = this.initialY + dy
                } else {
                    newHeight = minHeight
                    newY = this.initialY + (this.initialHeight - minHeight)
                }
            } else if (this.resizeDir === 'bl') {
                newWidth = this.initialWidth - dx
                newHeight = this.initialHeight + dy
                if (newWidth >= minWidth) {
                    newX = this.initialX + dx
                } else {
                    newWidth = minWidth
                    newX = this.initialX + (this.initialWidth - minWidth)
                }
            } else if (this.resizeDir === 'tl') {
                newWidth = this.initialWidth - dx
                newHeight = this.initialHeight - dy

                if (newWidth >= minWidth) {
                    newX = this.initialX + dx
                } else {
                    newWidth = minWidth
                    newX = this.initialX + (this.initialWidth - minWidth)
                }

                if (newHeight >= minHeight) {
                    newY = this.initialY + dy
                } else {
                    newHeight = minHeight
                    newY = this.initialY + (this.initialHeight - minHeight)
                }
            }

            if (newWidth < minWidth) newWidth = minWidth
            if (newHeight < minHeight) newHeight = minHeight

            this.width = newWidth
            this.height = newHeight
            this.x = newX
            this.y = newY
        },
        stopResize() {
            this.isResizing = false
            this.resizeDir = ''
            window.removeEventListener('mousemove', this.onResize)
            window.removeEventListener('mouseup', this.stopResize)
        },
        bringToFront() {
            this.zIndex = 2000
        },
        show() {
            this.$emit('update:visible', true)
            this.isMinimized = false
            this.bringToFront()
        },
    },
    beforeDestroy() {
        window.removeEventListener('mousemove', this.onDrag)
        window.removeEventListener('mouseup', this.stopDrag)
        window.removeEventListener('mousemove', this.onResize)
        window.removeEventListener('mouseup', this.stopResize)
    },
}
</script>

<style scoped lang="scss">
.draggable-postit {
    font-family: 'Kalam Regular';
    border-radius: 4px;
    box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.05);
    min-width: 200px;
    rotate: -2.5deg;
    position: fixed;
    width: min(350px, 40vw);
    height: min(282px, 35vh);
    background: #fff9be;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    user-select: none;
    left: 50%;
    top: 110px;
    z-index: 1000;
    transform: translateX(-50%);

    transition:
        width 0.3s ease,
        height 0.3s ease;

    @media (max-width: 1024px) {
        top: 80px;
        rotate: 0deg;
    }

    &.no-transition {
        transition: none !important;
    }

    &.minimized {
        width: 150px !important;
        height: 32px !important;
        cursor: pointer;
    }
}

.postit-header {
    height: 32px;
    background: rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    cursor: move;
}

.drag-handle {
    opacity: 0.5;
    display: flex;
    align-items: center;
}

.header-actions {
    display: flex;
    gap: 4px;
}

.btn-action {
    background: transparent;
    border: none;
    padding: 2px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    opacity: 0.6;
    min-width: max-content;

    &:hover {
        opacity: 1;
        background: rgba(0, 0, 0, 0.1);
    }
}

.postit-body {
    padding: 12px;
    flex: 1;
    display: flex;
    flex-direction: column;
}

.postit-textarea {
    width: 100%;
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    resize: none;
    font-family: 'Kalam Regular', cursive, sans-serif;
    font-size: 16px;
    line-height: 1.4;
    color: #334155 !important;

    &::placeholder {
        color: #94a3b8;
    }

    &[readonly] {
        cursor: not-allowed;
    }
}

.postit-minimized-label {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    text-transform: uppercase;
}

.draggable-postit::after {
    content: '';
    position: absolute;
    bottom: -20px;
    background: radial-gradient(68.57% 68.57% at 50.16% 0%, rgba(0, 0, 0, 0.2) 11.98%, rgba(0, 0, 0, 0) 76.04%),
        radial-gradient(45.71% 45.71% at 31.39% -20%, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 100%);
    width: 100%;
    height: 20px;
    left: 0;
}

/* Resize Handles Styling */
.resize-handle {
    position: absolute;
    width: 20px;
    height: 20px;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6366f1;

    &::before {
        content: '';
        position: absolute;
        width: 8px;
        height: 8px;
        opacity: 0;
        transition:
            opacity 0.2s ease,
            border-color 0.2s ease;
    }
}

.draggable-postit:hover .resize-handle::before {
    opacity: 0.3;
}

.resize-handle:hover::before {
    opacity: 1 !important;
    color: #4f46e5;
}

.top-left {
    top: 0;
    left: 0;
    cursor: nwse-resize;

    &::before {
        top: 4px;
        left: 4px;
        border-top: 2px solid currentColor;
        border-left: 2px solid currentColor;
    }
}

.top-right {
    top: 0;
    right: 0;
    cursor: nesw-resize;

    &::before {
        top: 4px;
        right: 4px;
        border-top: 2px solid currentColor;
        border-right: 2px solid currentColor;
    }
}

.bottom-left {
    bottom: 0;
    left: 0;
    cursor: nesw-resize;

    &::before {
        bottom: 4px;
        left: 4px;
        border-bottom: 2px solid currentColor;
        border-left: 2px solid currentColor;
    }
}

.bottom-right {
    bottom: 0;
    right: 0;
    cursor: nwse-resize;

    &::before {
        bottom: 4px;
        right: 4px;
        border-bottom: 2px solid currentColor;
        border-right: 2px solid currentColor;
    }
}
</style>
